// Settings.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import AdminNavbar from "./AdminNavbar";
import { FaSyncAlt, FaCheckCircle, FaTimesCircle, FaPlay, FaStop } from "react-icons/fa";

// ✅ bulk runner (posts missing NIPs into snapshots)
import { generateSnapshotForNip, getAnyTokenFromStorage } from "../api/transparentdata/kontreport/bulkSnapshotRunner";

// ---------------- styles ----------------
const Container = styled.div`
  min-height: 100vh;
  background: #f2f2f2;
  font-family: "Inter", sans-serif;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  padding: 20px;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 18px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0 0 6px 0;
`;

const SubTitle = styled.p`
  margin: 0 0 14px 0;
  opacity: 0.75;
  font-size: 14px;
  line-height: 1.4;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-start;
  margin-top: 10px;
`;

const Button = styled.button`
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #009fb7;
  color: white;
  transition: all 0.2s ease;

  &:hover {
    background: #007f94;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonGhost = styled(Button)`
  background: #e5e7eb;
  color: #111827;

  &:hover {
    background: #d1d5db;
  }
`;

const ButtonDanger = styled(Button)`
  background: #ef4444;

  &:hover {
    background: #dc2626;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #eee;
  margin: 14px 0;
`;

const InfoBox = styled.div`
  background: #e0f2fe;
  color: #075985;
  border: 1px solid #bae6fd;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.4;
`;

const Tabs = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const Tab = styled.button`
  border: 1px solid #ddd;
  background: ${(p) => (p.$active ? "#009fb7" : "white")};
  color: ${(p) => (p.$active ? "white" : "#333")};
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 800;

  &:hover {
    opacity: 0.9;
  }
`;

const MiniStats = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const StatPill = styled.div`
  background: #f9fafb;
  border: 1px solid #eee;
  border-radius: 999px;
  padding: 8px 12px;
  font-weight: 800;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;

  input {
    flex: 1;
    min-width: 240px;
    padding: 10px 12px;
    font-size: 14px;
    border-radius: 10px;
    border: 1px solid #d6d6d6;
    outline: none;

    &:focus {
      border-color: #009fb7;
      box-shadow: 0 0 0 3px rgba(0, 159, 183, 0.15);
    }
  }
`;

const TableWrap = styled.div`
  overflow: auto;
  margin-top: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eee;

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #eee;
    text-align: left;
    font-size: 14px;
    white-space: nowrap;
    vertical-align: top;
  }

  th {
    background: #f9f9f9;
    font-weight: 900;
  }
`;

const PaginationBar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
`;

const PageControls = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const PageBtn = styled.button`
  border: 1px solid #ddd;
  background: ${(p) => (p.$active ? "#009fb7" : "white")};
  color: ${(p) => (p.$active ? "white" : "#333")};
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 800;
  min-width: 42px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageSizeSelect = styled.select`
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #ddd;
  font-weight: 800;
  cursor: pointer;
`;

const BulkPanel = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f9fafb;
`;

const BulkRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;

  input {
    width: 220px;
    padding: 10px 12px;
    font-size: 14px;
    border-radius: 10px;
    border: 1px solid #d6d6d6;
    outline: none;

    &:focus {
      border-color: #009fb7;
      box-shadow: 0 0 0 3px rgba(0, 159, 183, 0.15);
    }
  }
`;

const ProgressBarWrap = styled.div`
  margin-top: 10px;
  height: 10px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  width: ${(p) => p.$pct}%;
  background: #009fb7;
  transition: width 0.2s ease;
`;

const Muted = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.35;
`;

// ---------------- helpers ----------------
const API_BASE = "https://server229256.nazwa.pl";




function getPageWindow(current, total, windowSize = 7) {
  // returns an array of page numbers with ellipsis markers as null
  if (total <= windowSize) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = [];
  const left = Math.max(1, current - 2);
  const right = Math.min(total, current + 2);

  pages.push(1);
  if (left > 2) pages.push(null);

  for (let p = left; p <= right; p++) {
    if (p !== 1 && p !== total) pages.push(p);
  }

  if (right < total - 1) pages.push(null);
  pages.push(total);

  // de-dup edge overlaps
  return pages.filter((p, idx, arr) => !(p === null && arr[idx - 1] === null));
}

function normalizeNip(nip) {
  return String(nip || "").replace(/\D/g, "").slice(0, 10);
}

function isValidNip(nip) {
  return /^\d{10}$/.test(normalizeNip(nip));
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// ---------------- component ----------------
const Settings = () => {
  const navigate = useNavigate();

  // compare
  const [loading, setLoading] = useState(false);
  const [compareResult, setCompareResult] = useState(null);
  const [activeTab, setActiveTab] = useState("matched"); // matched | onlyInKliencis | onlyInSnapshots
  const [nipSearch, setNipSearch] = useState("");

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // bulk runner
  const [bulkCount, setBulkCount] = useState(50);
  const [bulkDelayMs, setBulkDelayMs] = useState(0);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkIndex, setBulkIndex] = useState(0);
  const [bulkOk, setBulkOk] = useState(0);
  const [bulkFail, setBulkFail] = useState(0);
  const [bulkSkipped, setBulkSkipped] = useState(0);
  const [bulkLogs, setBulkLogs] = useState([]); // newest first
  const abortRef = useRef({ aborted: false });

  useEffect(() => {
    const adminUser = JSON.parse(localStorage.getItem("adminUser"));
    if (!adminUser || adminUser.userRole !== "SUPERADMIN") {
      toast.error("Brak dostępu.");
      navigate("/adminpanel/adminlogin");
    }
  }, [navigate]);

  const runCompare = async () => {
    try {
      setLoading(true);
      setCompareResult(null);

      const adminToken = localStorage.getItem("adminToken");
      if (!adminToken) {
        toast.error("Brak tokenu. Zaloguj się ponownie.");
        navigate("/adminpanel/adminlogin");
        return;
      }

      const res = await axios.get(`${API_BASE}/api/admin/compare-nips`, {
        headers: { Authorization: adminToken },
      });

      setCompareResult(res.data);
      setActiveTab("matched");
      setNipSearch("");
      setPage(1);

      // reset bulk state every time we refresh compare
      setBulkRunning(false);
      abortRef.current.aborted = false;
      setBulkIndex(0);
      setBulkOk(0);
      setBulkFail(0);
      setBulkSkipped(0);
      setBulkLogs([]);

      toast.success("Porównanie zakończone ✅");
    } catch (err) {
      console.error(err);
      toast.error("Nie udało się wykonać porównania NIP.");
    } finally {
      setLoading(false);
    }
  };

  // when tab/search/pageSize changes -> reset page
  useEffect(() => {
    setPage(1);
  }, [activeTab, nipSearch, pageSize]);

  const filteredList = useMemo(() => {
    if (!compareResult) return [];
    const list = compareResult[activeTab] || [];
    const q = nipSearch.replace(/\D/g, "");
    if (!q) return list;
    return list.filter((nip) => String(nip).includes(q));
  }, [compareResult, activeTab, nipSearch]);

  const totalRows = filteredList.length;

  const totalPages = useMemo(() => {
    if (!totalRows) return 1;
    return Math.max(1, Math.ceil(totalRows / pageSize));
  }, [totalRows, pageSize]);

  const safePage = Math.min(Math.max(page, 1), totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredList.slice(start, start + pageSize);
  }, [filteredList, safePage, pageSize]);

  const pageNumbers = useMemo(
    () => getPageWindow(safePage, totalPages, 7),
    [safePage, totalPages]
  );

  const fromRow = totalRows ? (safePage - 1) * pageSize + 1 : 0;
  const toRow = totalRows ? Math.min(totalRows, safePage * pageSize) : 0;

  const missingInSnapshots = useMemo(() => {
    const arr = compareResult?.onlyInKliencis || [];
    // clean + de-dup
    const seen = new Set();
    const out = [];
    for (const n of arr) {
      const c = normalizeNip(n);
      if (!isValidNip(c)) continue;
      if (!seen.has(c)) {
        seen.add(c);
        out.push(c);
      }
    }
    return out;
  }, [compareResult]);

  const bulkTargetList = useMemo(() => {
    const n = Number(bulkCount) || 0;
    if (n <= 0) return [];
    return missingInSnapshots.slice(0, Math.min(n, missingInSnapshots.length));
  }, [missingInSnapshots, bulkCount]);

  const bulkTotal = bulkTargetList.length;

  const bulkPct = useMemo(() => {
    if (!bulkTotal) return 0;
    const done = Math.min(bulkIndex, bulkTotal);
    return Math.round((done / bulkTotal) * 100);
  }, [bulkIndex, bulkTotal]);

  const addLog = (entry) => {
    setBulkLogs((prev) => [entry, ...prev].slice(0, 500)); // keep last 500
  };

  const stopBulk = () => {
    abortRef.current.aborted = true;
    setBulkRunning(false);
    toast("Zatrzymuję proces…");
  };


const markProcessed = async (nip) => {
  const adminToken = localStorage.getItem("adminToken");
  const clean = normalizeNip(nip);

  if (!adminToken) {
    addLog({ ts: new Date().toISOString(), nip: clean || nip, status: "WARN", msg: "Brak adminToken" });
    return;
  }
  if (!isValidNip(clean)) {
    addLog({ ts: new Date().toISOString(), nip: clean || nip, status: "WARN", msg: "Niepoprawny NIP — nie ustawiono status=true" });
    return;
  }

  try {
    await axios.put(
      `${API_BASE}/api/klienci/${clean}/status`,
      { status: true },
      { headers: { Authorization: adminToken } }
    );

    addLog({ ts: new Date().toISOString(), nip: clean, status: "CHECKED", msg: "status=true" });

    // ✅ remove from UI arrays safely
    setCompareResult((prev) => {
      if (!prev) return prev;

      const filterOut = (arr) =>
        (arr || []).filter((x) => normalizeNip(x) !== clean);

      return {
        ...prev,
        onlyInKliencis: filterOut(prev.onlyInKliencis),
        matched: filterOut(prev.matched),               // optional safety
        onlyInSnapshots: filterOut(prev.onlyInSnapshots), // optional safety
        counts: {
          ...(prev.counts || {}),
          onlyInKliencis: Math.max(0, (prev.counts?.onlyInKliencis || 0) - 1),
        },
      };
    });
  } catch (e) {
    addLog({
      ts: new Date().toISOString(),
      nip: clean,
      status: "WARN",
      msg: `Nie udało się ustawić status=true: ${e?.message || String(e)}`,
    });
  }
};



const startBulk = async () => {
  if (!compareResult) {
    toast.error("Najpierw uruchom porównanie NIP.");
    return;
  }
  if (missingInSnapshots.length === 0) {
    toast("Brak NIPów do uzupełnienia (snapshoty kompletne).");
    return;
  }
  if (bulkTotal === 0) {
    toast.error("Wpisz liczbę NIPów do uzupełnienia (np. 50).");
    return;
  }

  const token = getAnyTokenFromStorage(); // adminToken first, fallback token, strips "Bearer "
  if (!token) {
    toast.error("Brak tokenu (adminToken/token). Zaloguj się ponownie.");
    return;
  }

  const isSkipStatus = (status, msg) => {
    const s = String(status || "").toLowerCase();
    const m = String(msg || "").toLowerCase();
    return (
      s.startsWith("skip") ||
      s === "invalid_nip" ||
      s === "skip_invalid_vendor_nip" ||
      m.includes("nip jest niepoprawny") ||
      m.includes("invalid nip")
    );
  };

  abortRef.current.aborted = false;
  setBulkRunning(true);
  setBulkIndex(0);
  setBulkOk(0);
  setBulkFail(0);
  setBulkSkipped(0);
  setBulkLogs([]);

  toast.success(`Start: uzupełniam ${bulkTotal} NIPów…`);

for (let i = 0; i < bulkTargetList.length; i++) {
  if (abortRef.current.aborted) break;

  const nip = bulkTargetList[i];
  setBulkIndex(i + 1);

  try {
    const result = await generateSnapshotForNip({ nip, token });

    const ok = result?.ok === true;
    const status = result?.status || "unknown";
    const msg =
      typeof result === "string"
        ? result
        : result?.message || status;

    if (ok) {
      setBulkOk((x) => x + 1);
      addLog({ ts: new Date().toISOString(), nip, status: "OK", msg });
      await markProcessed(nip); // ✅ ALWAYS mark processed
    } else if (isSkipStatus(status, msg)) {
      setBulkSkipped((x) => x + 1);
      addLog({ ts: new Date().toISOString(), nip, status: "SKIP", msg });
      await markProcessed(nip); // ✅ mark processed even if skipped
    } else {
      setBulkFail((x) => x + 1);
      addLog({ ts: new Date().toISOString(), nip, status: "FAIL", msg });
      await markProcessed(nip); // ✅ mark processed even if failed
    }
  } catch (e) {
    const msg = e?.message || String(e);
    setBulkFail((x) => x + 1);
    addLog({ ts: new Date().toISOString(), nip, status: "FAIL", msg });
    await markProcessed(nip); // ✅ mark processed even on thrown error
  } finally {
    const d = Number(bulkDelayMs) || 0;
    if (d > 0) await sleep(Math.min(d, 60000));
  }
}


  setBulkRunning(false);

  if (abortRef.current.aborted) toast("Proces zatrzymany.");
  else toast.success("Proces zakończony ✅");
};


  return (
    <Container>
      <AdminNavbar />
      <Content>
        <Card>
          <Title>Ustawienia — Weryfikacja NIP</Title>
          <SubTitle>
            1) Kliknij <b>Uruchom porównanie</b> aby sprawdzić różnice między{" "}
            <b>kliencis.nip</b> a <b>CompanySnapshot.meta.nip</b>. <br />
            2) W zakładce <b>Brak w snapshotach</b> uruchom <b>uzupełnianie snapshotów</b>{" "}
            dla brakujących NIPów.
          </SubTitle>

          <InfoBox>
            Porównanie zwraca listy: <b>matched</b>, <b>onlyInKliencis</b> (brak w
            snapshotach) i <b>onlyInSnapshots</b> (brak w kliencis).
          </InfoBox>

          <Actions>
            <Button type="button" onClick={runCompare} disabled={loading}>
              <FaSyncAlt />
              {loading ? "Porównuję..." : "Uruchom porównanie"}
            </Button>
          </Actions>

          {compareResult && (
            <>
              <Divider />

              <MiniStats>
                <StatPill>Kliencis: {compareResult.counts?.kliencis ?? 0}</StatPill>
                <StatPill>Snapshots: {compareResult.counts?.snapshots ?? 0}</StatPill>
                <StatPill>
                  <FaCheckCircle /> Matched: {compareResult.counts?.matched ?? 0}
                </StatPill>
                <StatPill>
                  <FaTimesCircle /> Only in kliencis:{" "}
                  {compareResult.counts?.onlyInKliencis ?? 0}
                </StatPill>
                <StatPill>
                  <FaTimesCircle /> Only in snapshots:{" "}
                  {compareResult.counts?.onlyInSnapshots ?? 0}
                </StatPill>
              </MiniStats>

              <Tabs>
                <Tab $active={activeTab === "matched"} onClick={() => setActiveTab("matched")}>
                  Matched
                </Tab>
                <Tab
                  $active={activeTab === "onlyInKliencis"}
                  onClick={() => setActiveTab("onlyInKliencis")}
                >
                  Brak w snapshotach
                </Tab>
                <Tab
                  $active={activeTab === "onlyInSnapshots"}
                  onClick={() => setActiveTab("onlyInSnapshots")}
                >
                  Brak w kliencis
                </Tab>
              </Tabs>

              {/* ✅ bulk runner panel only on "missing in snapshots" */}
              {activeTab === "onlyInKliencis" && (
                <BulkPanel>
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>
                    Uzupełnianie snapshotów dla brakujących NIPów
                  </div>

                  <Muted>
                    Zostaną przetworzone NIPy z listy <b>Brak w snapshotach</b> (po
                    weryfikacji — tylko 10 cyfr). Snapshoty są zapisywane tak samo jak w raporcie
                    (cache + vendor APIs + POST do <code>/api/company-snapshots</code>).
                  </Muted>

                  <BulkRow style={{ marginTop: 10 }}>
                    <div style={{ fontWeight: 900 }}>Ile NIPów uruchomić:</div>
                    <input
                      type="number"
                      min={1}
                      max={missingInSnapshots.length || 1}
                      value={bulkCount}
                      onChange={(e) => setBulkCount(e.target.value)}
                      disabled={bulkRunning}
                    />

                    <div style={{ fontWeight: 900 }}>Opóźnienie (ms):</div>
                    <input
                      type="number"
                      min={0}
                      max={60000}
                      value={bulkDelayMs}
                      onChange={(e) => setBulkDelayMs(e.target.value)}
                      disabled={bulkRunning}
                    />

                    <Button
                      type="button"
                      onClick={startBulk}
                      disabled={bulkRunning || !compareResult || bulkTotal === 0}
                      title={
                        !compareResult
                          ? "Najpierw uruchom porównanie"
                          : bulkTotal === 0
                          ? "Brak NIPów do uruchomienia"
                          : ""
                      }
                    >
                      <FaPlay />
                      Start uzupełniania ({bulkTotal})
                    </Button>

                    <ButtonDanger type="button" onClick={stopBulk} disabled={!bulkRunning}>
                      <FaStop />
                      Stop
                    </ButtonDanger>

                    <ButtonGhost
                      type="button"
                      disabled={bulkRunning || bulkLogs.length === 0}
                      onClick={() => {
                        // copy logs as CSV
                        const rows = [
                          ["ts", "nip", "status", "msg"],
                          ...bulkLogs
                            .slice()
                            .reverse()
                            .map((x) => [x.ts, x.nip, x.status, String(x.msg || "")]),
                        ];
                        const csv = rows
                          .map((r) =>
                            r
                              .map((v) =>
                                `"${String(v).replace(/"/g, '""').replace(/\n/g, " ")}"`
                              )
                              .join(",")
                          )
                          .join("\n");
                        navigator.clipboard
                          .writeText(csv)
                          .then(() => toast.success("Skopiowano logi (CSV) ✅"))
                          .catch(() => toast.error("Nie udało się skopiować logów."));
                      }}
                    >
                      Skopiuj logi (CSV)
                    </ButtonGhost>
                  </BulkRow>

                  <MiniStats style={{ marginTop: 12 }}>
                    <StatPill>Do zrobienia: {bulkTotal}</StatPill>
                    <StatPill>Postęp: {bulkPct}%</StatPill>
                    <StatPill>
                      <FaCheckCircle /> OK: {bulkOk}
                    </StatPill>
                    <StatPill>
                      <FaTimesCircle /> FAIL: {bulkFail}
                    </StatPill>
                    <StatPill>SKIP: {bulkSkipped}</StatPill>
                  </MiniStats>

                  <ProgressBarWrap>
                    <ProgressBarFill $pct={bulkPct} />
                  </ProgressBarWrap>

                  {bulkRunning && (
                    <Muted>
                      Przetwarzam: <b>{bulkIndex}</b> / {bulkTotal}
                    </Muted>
                  )}

                  {bulkLogs.length > 0 && (
                    <TableWrap>
                      <Table>
                        <thead>
                          <tr>
                            <th>czas</th>
                            <th>NIP</th>
                            <th>status</th>
                            <th>info</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkLogs.slice(0, 50).map((l, idx) => (
                            <tr key={`${l.ts}-${l.nip}-${idx}`}>
                              <td style={{ fontSize: 12, color: "#6b7280" }}>
                                {new Date(l.ts).toLocaleString("pl-PL")}
                              </td>
                              <td>{l.nip}</td>
                              <td style={{ fontWeight: 900 }}>{l.status}</td>
                              <td style={{ whiteSpace: "normal", maxWidth: 520 }}>{l.msg}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <Muted>Pokazuję ostatnie 50 wpisów (najnowsze na górze).</Muted>
                    </TableWrap>
                  )}
                </BulkPanel>
              )}

              <SearchRow>
                <input
                  placeholder="Szukaj NIP (np. 1234567890)"
                  value={nipSearch}
                  onChange={(e) => setNipSearch(e.target.value)}
                />
              </SearchRow>

              <PaginationBar>
                <div style={{ fontWeight: 800, opacity: 0.8 }}>
                  {totalRows ? (
                    <>
                      Wyniki: {fromRow}-{toRow} z {totalRows}
                    </>
                  ) : (
                    "Wyniki: 0"
                  )}
                </div>

                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 800 }}>Na stronę:</div>
                  <PageSizeSelect
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={250}>250</option>
                    <option value={500}>500</option>
                  </PageSizeSelect>
                </div>
              </PaginationBar>

              <PageControls style={{ marginTop: 10 }}>
                <PageBtn onClick={() => setPage(1)} disabled={safePage === 1} title="Pierwsza">
                  «
                </PageBtn>
                <PageBtn
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  title="Poprzednia"
                >
                  ‹
                </PageBtn>

                {pageNumbers.map((p, idx) =>
                  p === null ? (
                    <div key={`dots-${idx}`} style={{ padding: "0 6px", opacity: 0.6 }}>
                      …
                    </div>
                  ) : (
                    <PageBtn key={p} $active={p === safePage} onClick={() => setPage(p)}>
                      {p}
                    </PageBtn>
                  )
                )}

                <PageBtn
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  title="Następna"
                >
                  ›
                </PageBtn>
                <PageBtn
                  onClick={() => setPage(totalPages)}
                  disabled={safePage === totalPages}
                  title="Ostatnia"
                >
                  »
                </PageBtn>
              </PageControls>

              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>NIP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((nip, idx) => (
                      <tr key={`${nip}-${idx}`}>
                        <td>{(safePage - 1) * pageSize + idx + 1}</td>
                        <td>{nip}</td>
                      </tr>
                    ))}

                    {pageItems.length === 0 && (
                      <tr>
                        <td colSpan={2} style={{ opacity: 0.7 }}>
                          Brak wyników.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </TableWrap>
            </>
          )}
        </Card>
      </Content>
    </Container>
  );
};

export default Settings;
